import { useState, type FormEvent } from 'react';
import { Modal } from './Modal';
import type { Column } from '../../types/board';

interface DeleteColumnModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDelete: (migrateToColumnId?: string) => void;
  column: Column | null;
  otherColumns: Column[];
  isLoading?: boolean;
}

export function DeleteColumnModal({
  isOpen,
  onClose,
  onDelete,
  column,
  otherColumns,
  isLoading = false,
}: DeleteColumnModalProps): JSX.Element {
  const [migrateCards, setMigrateCards] = useState(false);
  const [targetColumnId, setTargetColumnId] = useState('');

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!isLoading) {
      onDelete(migrateCards && targetColumnId ? targetColumnId : undefined);
    }
  };

  if (!column) return <></>;

  const hasCards = column.cards.length > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete Column">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <p className="text-yellow-700 dark:text-yellow-400">
            Are you sure you want to delete the column{' '}
            <span className="font-semibold">{column.name}</span>?
          </p>
          {hasCards && (
            <p className="text-yellow-600 dark:text-yellow-500 mt-2 text-sm">
              This column contains {column.cards.length} card
              {column.cards.length > 1 ? 's' : ''}.
            </p>
          )}
        </div>

        {hasCards && otherColumns.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="migrateCards"
                checked={migrateCards}
                onChange={(e) => setMigrateCards(e.target.checked)}
                disabled={isLoading}
                className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <label
                htmlFor="migrateCards"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Migrate cards to another column
              </label>
            </div>

            {migrateCards && (
              <select
                value={targetColumnId}
                onChange={(e) => setTargetColumnId(e.target.value)}
                disabled={isLoading}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select column...</option>
                {otherColumns.map((col) => (
                  <option key={col.id} value={col.id}>
                    {col.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading || (hasCards && migrateCards && !targetColumnId)}
            className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Deleting...' : 'Delete Column'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
